// src/utils/reservationWhatsapp.js
//
// Requerimiento "WhatsApp para Reservaciones y Lista de Espera": reutiliza
// el MISMO mecanismo de envío que ya usa Cierre Diario (ControlFecha.js) —
// POST a `${IP}/tavpos/backend/enviar_whatsapp.php` (proveedor TextMeBot),
// con el mismo contrato de payload/respuesta. No se creó un envío nuevo ni
// independiente: solo se arma el mensaje distinto (por template) y se
// reutiliza el mismo POST.
//
// Lo único nuevo del lado del backend es lo que no existía antes:
//   - teamsreservation.json  → números del equipo, por propiedad.
//   - whatsapp_historial.php → historial de envíos, por propiedad (CSV).
// CORREGIDO: ambos se sirven desde el MISMO backend/dominio que ya usan
// config.json y enviar_whatsapp.php (`${IP}/tavpos/backend/...`) — no desde
// un dominio/ruta "tavpos" aparte, que no existe en producción (la app
// corre en un dominio distinto al backend PHP; usar una ruta que no está
// deployada ahí causaba "Failed to fetch"). El archivo/script en sí puede
// desarrollarse en este repo, pero debe DESPLEGARSE junto a config.json y
// enviar_whatsapp.php, en la misma carpeta backend/data/<propiedad>/ del
// servidor real.

import { IP } from "../configuracion";

// ─────────────────────────────────────────────────────────────────────────
// Templates iniciales
// ─────────────────────────────────────────────────────────────────────────
// target: a quién está pensado el template — solo se usa para pre-filtrar
// la lista en el modal según el destinatario elegido; el usuario puede
// editar el texto libremente antes de enviar (requerimiento punto 4).
export const RESERVATION_WHATSAPP_TEMPLATES = [
  {
    id: "confirmacion_cliente",
    label: "Confirmación de reservación",
    target: "cliente",
    text:
      "Hola {cliente} 👋\n\n" +
      "Tu reservación está *confirmada*:\n\n" +
      "📅 Fecha: {fecha}\n" +
      "🕐 Hora: {hora}\n" +
      "👥 Personas: {personas}\n" +
      "📍 Área: {area}\n\n" +
      "Te esperamos en {propiedad}.",
  },
  {
    id: "recordatorio_cliente",
    label: "Recordatorio de reservación",
    target: "cliente",
    text:
      "Hola {cliente} 👋\n\n" +
      "Te recordamos tu reservación:\n\n" +
      "📅 Fecha: {fecha}\n" +
      "🕐 Hora: {hora}\n" +
      "👥 Personas: {personas}\n" +
      "📍 Área: {area}\n\n" +
      "¡Te esperamos!",
  },
  {
    id: "modificacion_cliente",
    label: "Modificación de reservación",
    target: "cliente",
    text:
      "Hola {cliente} 👋\n\n" +
      "Tu reservación fue *modificada*. Estos son los nuevos datos:\n\n" +
      "📅 Fecha: {fecha}\n" +
      "🕐 Hora: {hora}\n" +
      "👥 Personas: {personas}\n" +
      "📍 Área: {area}\n\n" +
      "Cualquier duda, contáctanos.",
  },
  {
    id: "cancelacion_cliente",
    label: "Cancelación",
    target: "cliente",
    text:
      "Hola {cliente},\n\n" +
      "Tu reservación del {fecha} a las {hora} para {personas} personas ha sido " +
      "*cancelada*.\n\n" +
      "Si fue un error o deseas reagendar, contáctanos.",
  },
  {
    id: "agregado_espera_cliente",
    label: "Cliente agregado a lista de espera",
    target: "cliente",
    text:
      "Hola {cliente} 👋\n\n" +
      "Quedaste anotado en la lista de espera para {personas} personas.\n" +
      "Te avisaremos por aquí en cuanto tengamos mesa disponible.\n\n" +
      "Gracias por tu paciencia 🙏",
  },
  {
    id: "mesa_disponible_cliente",
    label: "Mesa disponible / confirmar asistencia",
    target: "cliente",
    text:
      "Hola {cliente} 👋\n\n" +
      "¡Ya tenemos mesa disponible para ti! 🎉\n" +
      "Por favor confirma tu asistencia respondiendo este mensaje.\n\n" +
      "Te esperamos en los próximos minutos.",
  },
  {
    id: "nueva_reservacion_equipo",
    label: "Aviso de nueva reservación al equipo",
    target: "equipo",
    text:
      "🆕 *NUEVA RESERVACIÓN*\n\n" +
      "Cliente: {cliente}\n" +
      "Fecha: {fecha}\n" +
      "Hora: {hora}\n" +
      "Personas: {personas}\n" +
      "Teléfono: {telefono}\n" +
      "Notas: {notas}",
  },
  {
    id: "cambio_cancelacion_equipo",
    label: "Aviso de cambio o cancelación al equipo",
    target: "equipo",
    text:
      "⚠️ *CAMBIO / CANCELACIÓN DE RESERVACIÓN*\n\n" +
      "Cliente: {cliente}\n" +
      "Fecha: {fecha}\n" +
      "Hora: {hora}\n" +
      "Personas: {personas}\n" +
      "Teléfono: {telefono}\n" +
      "Notas: {notas}",
  },
];

// Todas las variables que puede llegar a usar un template, para que la UI
// pueda mostrar cuáles quedaron sin dato (no rompe nada, solo informa).
export const RESERVATION_TEMPLATE_VARS = [
  "cliente", "fecha", "hora", "personas", "area", "telefono", "notas", "propiedad",
];

// Sustituye {variable} por su valor. Si falta el dato, lo deja en blanco en
// vez de imprimir "undefined" — evita mensajes rotos si un campo no aplica
// (p.ej. un walk-in sin "área" asignada todavía).
export function fillTemplate(text, vars = {}) {
  if (typeof text !== "string") return "";
  return text.replace(/\{(\w+)\}/g, (_match, key) => {
    const value = vars[key];
    return value === undefined || value === null ? "" : String(value);
  });
}

// Arma el set de variables a partir de un registro de Reservaciones/Lista
// de Espera (mismo shape que usa VentanaListaEspera.js: customer_name,
// phone, party_size, scheduled_time, notes) y la propiedad activa.
export function buildReservationTemplateVars(item = {}, property = "") {
  let fecha = "";
  let hora = "";
  if (item.scheduled_time) {
    const d = new Date(item.scheduled_time);
    if (!isNaN(d.getTime())) {
      fecha = d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
      hora = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
    }
  }
  return {
    cliente: item.customer_name || "Cliente",
    fecha,
    hora,
    personas: item.party_size ?? "",
    // No todos los registros de Lista de Espera/Reservaciones tienen un
    // área/mesa asignada todavía — se deja en blanco en ese caso, el
    // template igual queda legible ("Área: ").
    area: item.area_name || item.revenue_name || "",
    telefono: item.phone || "",
    notas: item.notes || "",
    propiedad: property || "",
  };
}

function getCurrentUserName() {
  // Mismo patrón ya usado en ControlFecha.js: App.js no pasa currentUser a
  // estos componentes, así que se lee del localStorage donde se guarda.
  try {
    const saved = localStorage.getItem("currentUser");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed?.nombre) return parsed.nombre;
    }
  } catch (error) {
    console.error("Error al leer currentUser para WhatsApp de Reservaciones:", error);
  }
  return "Usuario desconocido";
}

// ─────────────────────────────────────────────────────────────────────────
// Equipo (teamsreservation.json)
// ─────────────────────────────────────────────────────────────────────────
export async function fetchReservationTeam(property) {
  if (!property) return [];
  try {
    const res = await fetch(
      `${IP}/tavpos/backend/data/${encodeURIComponent(property)}/teamsreservation.json`
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data.team || []).filter((t) => t && t.activo !== false && t.telefono);
  } catch (error) {
    console.error("Error cargando teamsreservation.json:", error);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Envío — reutiliza enviar_whatsapp.php (mismo contrato que ControlFecha.js)
// ─────────────────────────────────────────────────────────────────────────
export async function sendReservationWhatsapp({ numeros, mensaje, property, tipo }) {
  if (!Array.isArray(numeros) || numeros.length === 0) {
    return { success: false, message: "No hay números destino." };
  }
  try {
    const response = await fetch(`${IP}/tavpos/backend/enviar_whatsapp.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        numero: numeros,
        mensaje,
        property,
        tipo, // p.ej. 'reservacion_confirmacion', 'reservacion_equipo', etc.
      }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result = await response.json();
    return {
      success: !!result.success,
      total: numeros.length,
      exitosos: result.total_enviados || 0,
      fallidos: result.total_fallidos || 0,
      detalles: result.resultados || [],
    };
  } catch (error) {
    console.error("Error enviando WhatsApp de Reservaciones:", error);
    return { success: false, message: error.message };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Historial (nuevo, whatsapp_historial.php — se despliega junto a
// enviar_whatsapp.php en el mismo backend)
// ─────────────────────────────────────────────────────────────────────────
export async function logReservationWhatsappHistory({
  property, queueId, cliente, mensaje, destinatario, estatus, templateId,
}) {
  try {
    const res = await fetch(`${IP}/tavpos/backend/whatsapp_historial.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        property,
        queue_id: queueId ?? "",
        cliente: cliente || "",
        mensaje,
        destinatario, // 'cliente' | 'equipo' | 'ambos'
        usuario: getCurrentUserName(),
        estatus, // 'enviado' | 'error' | 'parcial'
        template_id: templateId || "",
      }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (error) {
    // El historial es informativo — si falla, no debe tumbar el envío real.
    console.error("Error guardando historial de WhatsApp:", error);
    return { success: false, message: error.message };
  }
}

export async function fetchReservationWhatsappHistory(property, queueId) {
  if (!property) return [];
  try {
    const params = new URLSearchParams({ property });
    if (queueId !== undefined && queueId !== null && queueId !== "") {
      params.set("queue_id", queueId);
    }
    const res = await fetch(`${IP}/tavpos/backend/whatsapp_historial.php?${params.toString()}`);
    if (!res.ok) return [];
    const result = await res.json();
    return Array.isArray(result.data) ? result.data : [];
  } catch (error) {
    console.error("Error leyendo historial de WhatsApp:", error);
    return [];
  }
}

export { getCurrentUserName };
